import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import { AuthService } from "../auth/auth.service";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";

export interface TrialStatus {
  active: boolean;
  daysRemaining: number;
  endsAt?: string;
}

interface ApiResponseBody<T> {
  body?: T;
  message?: string;
}

/**
 * Hand-written on purpose, like CompanyWhatsappAdminApiService next door: these endpoints
 * (CompanyApi#getTrialStatus, CompanySolutionAdminApi#updateTrialEndsAt) are new on waba and the
 * checked-in swagger is only regenerated from a live server, so `npm run gn-waba` cannot produce
 * them yet. Delete this file and use the generated services once waba is redeployed and the
 * swagger is refreshed.
 */
@Injectable({ providedIn: "root" })
export class CompanyTrialStatusApiService {
  private readonly baseUrl = `${environment.wabaApiUrl}/v1/api/ui/company/trial-status`;
  private readonly adminBaseUrl = `${environment.wabaApiUrl}/v1/api/admin-ui/company/trial`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ "x-api-requestId": uuidv4(), "x-api-canal": "web" });
  }

  /** Trial status of the current user's own company. */
  getTrialStatus(): Observable<TrialStatus | undefined> {
    return this.getTrialStatusByCompanyId(this.authService.getCompanyId() ?? "");
  }

  /** Trial status of any company by its companyId — used by admin/staff screens. */
  getTrialStatusByCompanyId(companyId: string): Observable<TrialStatus | undefined> {
    return this.http
      .get<ApiResponseBody<TrialStatus>>(`${this.baseUrl}/${encodeURIComponent(companyId)}`, {
        headers: this.headers(),
        context: SILENT_HTTP_CONTEXT,
      })
      .pipe(map((response) => response?.body));
  }

  /** Admin-only: manually set (extend, shorten, or start) a company's trial end date. */
  updateTrialEndsAt(companyRef: string, endsAtIso: string): Observable<TrialStatus | undefined> {
    return this.http
      .patch<ApiResponseBody<TrialStatus>>(
        `${this.adminBaseUrl}/${encodeURIComponent(companyRef)}`,
        null,
        { headers: this.headers(), params: { endsAt: endsAtIso } },
      )
      .pipe(map((response) => response?.body));
  }

  /**
   * Admin-only, irreversible: permanently takes a company off trial tracking — same
   * trial-free state as an admin-created company from creation. Restores full access
   * immediately if the company is currently read-only from an expired trial.
   */
  convertToPermanentClient(companyRef: string): Observable<TrialStatus | undefined> {
    return this.http
      .post<ApiResponseBody<TrialStatus>>(
        `${this.adminBaseUrl}/${encodeURIComponent(companyRef)}/convert-to-client`,
        null,
        { headers: this.headers() },
      )
      .pipe(map((response) => response?.body));
  }
}
